/**
 * Daten fuer den Ernaehrungs-Ueberblick (F-35 C): je Tag Gegessenes, Plan,
 * Getraenke und ob Trainingstag ist. Trainingstag = an dem Tag gibt es ein
 * Trainingsprotokoll oder der aktive Plan sieht eine Einheit vor (F-34).
 */
import { db } from '@/db/schema';
import { gruppenPortionen, tagZeile, type TagEingabe } from '@/domain/ernaehrung/ueberblick';
import { pruefe, STANDARD_KRITERIEN, tagesScore } from '@/domain/ernaehrung/analyse';
import type { KennTag } from '@/domain/ernaehrung/kennwerte';
import { einheitenAm, planAm } from '@/domain/training/plan';
import type { LocalDate } from '@/domain/types';
import { addDays } from '@/lib/datetime';

export async function trainingstage(von: LocalDate, bis: LocalDate): Promise<Set<LocalDate>> {
  const [protokolle, plaene, einheiten] = await Promise.all([
    db.trainingsprotokolle.where('datum').between(von, bis, true, true).toArray(), db.trainingsplaene.toArray(), db.planEinheiten.toArray(),
  ]);
  const tage = new Set(protokolle.map((p) => p.datum));
  for (let d = von; d <= bis; d = addDays(d, 1)) {
    const plan = planAm(plaene, d);
    if (plan && einheitenAm(plan, einheiten, d).length) tage.add(d);
  }
  return tage;
}

export async function ernaehrungsTage(von: LocalDate, bis: LocalDate): Promise<TagEingabe[]> {
  const [gegessen, plan, getraenke, training] = await Promise.all([
    db.gegessen.where('datum').between(von, bis, true, true).toArray(),
    db.speiseplan.where('datum').between(von, bis, true, true).toArray(),
    db.getraenke.where('datum').between(von, bis, true, true).toArray(),
    trainingstage(von, bis),
  ]);
  const out: TagEingabe[] = [];
  for (let d = von; d <= bis; d = addDays(d, 1)) {
    const g = gegessen.find((x) => x.datum === d);
    const p = plan.find((x) => x.datum === d);
    out.push({ datum: d, training: training.has(d), getraenke: getraenke.filter((x) => x.datum === d), ...(g ? { gegessen: g } : {}), ...(p ? { plan: p } : {}) });
  }
  return out;
}

/** Tage mit Score und erfuellten Kriterien - Quelle fuer Key Results (F-35 E). */
export async function kennTage(von: LocalDate, bis: LocalDate): Promise<KennTag[]> {
  const [eingaben, lebensmittel, rezepte, e] = await Promise.all([
    ernaehrungsTage(von, bis), db.lebensmittel.toArray(), db.rezepte.toArray(), db.ernaehrungsEinstellungen.get('standard'),
  ]);
  const d = { lebensmittel: new Map(lebensmittel.map((l) => [l.id, l])), rezepte: new Map(rezepte.map((r) => [r.id, r])) };
  const kriterien = e?.kriterien ?? STANDARD_KRITERIEN;
  return eingaben.map((t) => {
    const zeile = tagZeile(t, d, e);
    const gruppen = gruppenPortionen(t.gegessen?.mahlzeiten ?? [], d);
    return {
      datum: t.datum, mitEintrag: zeile.mahlzeiten > 0, vollstaendig: zeile.vollstaendig, score: tagesScore(zeile, gruppen, kriterien, e?.spanne).score,
      erfuellt: new Set(kriterien.flatMap((k) => (pruefe(k, zeile, gruppen, e?.spanne)?.erfuellt ? [k.art] : []))),
    };
  });
}
